import { tr, usePartnerTranslation } from "../lib/language";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, Loader2 } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { parseApiError } from "../lib/api";
import { EMAIL_REGEX } from "../lib/constants";
import { useAuth, defaultPageFor } from "../lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LoginPage = () => {
  usePartnerTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem("voocly_remember_email");
    if (saved) {
      setEmail(saved);
      setRemember(true);
    }
  }, []);

  const validate = () => {
    const errs = { email: "", password: "" };
    if (!email) {
      errs.email = tr("Email is required.");
    } else if (!EMAIL_REGEX.test(email.trim())) {
      errs.email = tr("Please enter a valid email address.");
    }
    if (!password) errs.password = tr("Password is required.");
    return errs;
  };

  const errors = validate();
  const hasErrors = !!(errors.email || errors.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    setServerError("");
    if (hasErrors) return;

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
        remember,
      });
      if (remember) {
        localStorage.setItem("voocly_remember_email", email.trim());
      } else {
        localStorage.removeItem("voocly_remember_email");
      }
      const user = res.data?.user || { email: email.trim() };
      login({ user, remember });
      toast.success(tr("Signed in successfully"), {
        description: tr("Welcome back, {{value0}}!", { value0: user.full_name || user.email }),
      });
      // Default landing page depends on role.
      navigate(/^\/refund-requests(?:\?|$)/.test(location.state?.from || "") ? location.state.from : defaultPageFor(user.role));
    } catch (err) {
      const { code, message } = parseApiError(err, tr("Invalid email or password. Please try again."));
      const msg = code === "unauthenticated"
        ? "Invalid email or password. Please try again."
        : message;
      setServerError(msg);
      toast.error(tr("Sign in failed"), { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const emailError = touched.email ? errors.email : "";
  const passwordError = touched.password ? errors.password : "";

  return (
    <main
      data-testid="login-page"
      className="flex flex-1 items-center justify-center px-4 py-10 md:py-16"
    >
      <section className="humrahe-card w-full max-w-[720px] px-6 py-12 md:px-16 md:py-16">
        <header className="mb-10 text-center">
          <h1
            data-testid="login-heading"
            className="text-3xl font-bold tracking-tight text-[#0F1115] sm:text-4xl"
          >{tr("Log in to the Partner Portal")}</h1>
          <p className="mt-3 text-sm text-[#6B7280] sm:text-base">{tr("Welcome back! Please enter your details.")}</p>
        </header>

        <form
          data-testid="login-form"
          noValidate
          onSubmit={handleSubmit}
          className="mx-auto max-w-[460px] space-y-5"
        >
          {/* Email */}
          <div className="space-y-1.5">
            <div className="relative">
              <label htmlFor="email" className="humrahe-field-label">{tr("Email")}<span className="text-[#E5484D]">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                data-testid="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                placeholder={tr("Enter your Email")}
                className={`humrahe-field ${emailError ? "humrahe-field-error" : ""}`}
                aria-invalid={!!emailError}
                aria-describedby="email-error"
              />
            </div>
            {emailError && (
              <p
                id="email-error"
                data-testid="email-error"
                className="flex items-center gap-1.5 ps-1 text-xs font-medium text-[#E5484D]"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                {emailError}
              </p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <div className="relative">
              <label htmlFor="password" className="humrahe-field-label">{tr("Password")}<span className="text-[#E5484D]">*</span>
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                data-testid="password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                placeholder={tr("Enter your Password")}
                className={`humrahe-field ${passwordError ? "humrahe-field-error" : ""}`}
                aria-invalid={!!passwordError}
                aria-describedby="password-error"
              />
              <button
                type="button"
                data-testid="password-toggle-btn"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? tr("Hide password") : tr("Show password")}
                className="absolute end-4 top-1/2 -translate-y-1/2 text-[#6B7280] transition-colors hover:text-[#0F1115]"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {passwordError && (
              <p
                id="password-error"
                data-testid="password-error"
                className="flex items-center gap-1.5 ps-1 text-xs font-medium text-[#E5484D]"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                {passwordError}
              </p>
            )}
          </div>

          {/* Remember + Forgot */}
          <div className="flex items-center justify-between pt-1">
            <label
              htmlFor="remember"
              className="flex cursor-pointer items-center gap-2 text-sm text-[#0F1115]"
            >
              <Checkbox
                id="remember"
                data-testid="remember-me-checkbox"
                checked={remember}
                onCheckedChange={(v) => setRemember(!!v)}
                className="h-4 w-4 rounded-[4px] border-[#B6BAC2] data-[state=checked]:border-[#2476FF] data-[state=checked]:bg-[#2476FF]"
              />
              <span className="select-none">{tr("Remember me")}</span>
            </label>

            <Link
              data-testid="forgot-password-link"
              to="/forgot-password"
              className="humrahe-link text-sm font-semibold"
            >{tr("Forget Password?")}</Link>
          </div>

          {serverError && (
            <div
              data-testid="server-error"
              className="flex items-start gap-2 rounded-lg border border-[#FDD8D8] bg-[#FFF4F4] px-3 py-2.5 text-sm text-[#B42318]"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{serverError}</span>
            </div>
          )}

          <button
            type="submit"
            data-testid="sign-in-btn"
            disabled={submitting}
            className="humrahe-btn-primary mt-2 inline-flex h-12 w-full items-center justify-center rounded-lg text-sm font-semibold tracking-wide disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" />{tr("Signing in…")}</>
            ) : (
              tr("Sign in")
            )}
          </button>

          <p
            className="pt-2 text-center text-sm text-[#0F1115]"
            data-testid="signup-row"
          >{tr("or")}{" "}
            <Link
              to="/signup"
              data-testid="signup-link"
              className="humrahe-link font-semibold"
            >{tr("Sign up")}</Link>
          </p>
        </form>
      </section>
    </main>
  );
};

export default LoginPage;
